import React from "react";
import { Link } from "react-router-dom";
import { Building2, User, Landmark, ExternalLink, ArrowRight } from "lucide-react";

const TYPE_ICONS = {
    person: User,
    company: Building2,
    organization: Landmark,
};

// Same canonicalisation rules as the backend resolver (entity_aliases.py).
// Kept deliberately small and pragmatic: 90% of variants land on the same slug
// as the server. If we ever need stricter parity, the API also resolves on read.
const EXPLICIT = {
    "blackrock, inc.": "blackrock",
    "blackrock inc.": "blackrock",
    "blackrock inc": "blackrock",
    "blackrock, inc": "blackrock",
    "the vanguard group": "vanguard",
    "state street corporation": "state-street",
    "state street global advisors": "state-street",
    "ssga": "state-street",
    "ssga (state street)": "state-street",
    "u.s. food and drug administration": "us-fda",
    "us food and drug administration": "us-fda",
    "food and drug administration": "us-fda",
    "fda": "us-fda",
    "fda center for drug evaluation and research": "us-fda",
    "u.s. cdc": "us-cdc",
    "us cdc": "us-cdc",
    "cdc": "us-cdc",
    "centers for disease control and prevention": "us-cdc",
    "u.s. cdc division of diabetes translation": "us-cdc",
    "u.s. epa": "us-epa",
    "us epa": "us-epa",
    "epa": "us-epa",
    "u.s. environmental protection agency": "us-epa",
    "environmental protection agency": "us-epa",
    "u.s. nih": "us-nih",
    "nih": "us-nih",
    "national institutes of health": "us-nih",
    "u.s. usda": "usda",
    "us usda": "usda",
    "u.s. department of agriculture": "usda",
    "world health organisation": "world-health-organization",
    "world health organization": "world-health-organization",
    "who": "world-health-organization",
    "iarc": "international-agency-for-research-on-cancer-iarc",
    "international agency for research on cancer": "international-agency-for-research-on-cancer-iarc",
    "european food safety authority": "efsa",
    "european food safety authority (efsa)": "efsa",
    "the bbc": "bbc",
    "bbc news": "bbc",
    "world economic forum": "world-economic-forum",
    "the world economic forum": "world-economic-forum",
    "wef": "world-economic-forum",
    "novo nordisk a/s": "novo-nordisk",
    "eli lilly and company": "eli-lilly",
    "lilly": "eli-lilly",
    "bayer ag": "bayer",
    "bayer ag (monsanto)": "bayer",
    "bayer (monsanto)": "bayer",
    "monsanto": "bayer",
    "msci esg research": "msci",
    "c40": "c40-cities",
    "tyson foods, inc.": "tyson-foods",
    "tyson foods": "tyson-foods",
    "jbs": "jbs-sa",
    "jbs s.a.": "jbs-sa",
    "jbs sa": "jbs-sa",
    "cargill, incorporated": "cargill",
    "archer daniels midland": "adm-archer-daniels-midland",
    "adm": "adm-archer-daniels-midland",
    "adm (archer daniels midland)": "adm-archer-daniels-midland",
    "bunge limited": "bunge",
};

const SUFFIX_RE = /\s*[,]?\s*(s\.?a\.?|p\.?l\.?c\.?|gmbh|a\.?g\.?|n\.?v\.?|llc|ltd\.?|limited|holdings?|inc\.?|incorporated|corp\.?|corporation|co\.?|company|group)\s*$/i;
const PAREN_RE = /\([^)]*\)/g;

export function entitySlug(name) {
    if (!name) return "";
    const raw = name.trim();
    const low = raw.toLowerCase();
    if (EXPLICIT[low]) return EXPLICIT[low];
    let stripped = raw.replace(PAREN_RE, "").trim();
    for (let i = 0; i < 2; i++) {
        const next = stripped.replace(SUFFIX_RE, "").replace(/[,.]+$/, "").trim();
        if (next === stripped) break;
        stripped = next;
    }
    return stripped
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s-]/g, " ")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function EntityCard({ entity, index }) {
    const Icon = TYPE_ICONS[entity.type] || Building2;
    const slug = entitySlug(entity.name);
    return (
        <div data-testid={`entity-card-${index}`} className="bg-[#111827] border border-[#243044] rounded-xl p-5 hover:border-[#2F80ED]/40 transition-colors">
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#0B1220] border border-[#243044] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#56CCF2]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
                        <h4 className="text-sm font-semibold text-white">{entity.name}</h4>
                        {entity.type && (
                            <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] bg-[#0B1220] border border-[#243044] px-2 py-0.5 rounded-full">
                                {entity.type}
                            </span>
                        )}
                    </div>
                    {entity.role && (
                        <p className="text-xs text-[#56CCF2] mb-2">{entity.role}</p>
                    )}
                    {entity.bio && (
                        <p className="text-sm text-[#94A3B8] leading-relaxed mb-2">{entity.bio}</p>
                    )}
                    <div className="flex items-center gap-4 mt-1">
                        {slug && (
                            <Link
                                to={`/entity/${slug}`}
                                data-testid="entity-card-profile-link"
                                className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white transition-colors"
                            >
                                <ArrowRight className="w-3 h-3" /> Open entity profile
                            </Link>
                        )}
                        {entity.url && (
                            <a href={entity.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#56CCF2] transition-colors">
                                <ExternalLink className="w-3 h-3" /> Reference
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
